import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IContactDTO } from '@common-frontend-submodule/interfaces/contact/icontact.interface';
import { IContactCreationDTO } from '@common-frontend-submodule/interfaces/contact/icontact-creation.interface';
import { IContactUpdateDTO } from '@common-frontend-submodule/interfaces/contact/icontact-update.interface';
import { IGetContactsRequestDTO } from '@common-frontend-submodule/interfaces/contact/iget-contacts-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { ContactRoutes } from '@common-frontend-submodule/enums/routes/contact-routes.enum';

const apiClient = ApiClient.getInstance()

interface ContactContextProps {
  getById: (contactId: string) => Promise<IApiResponseGenericDTO<IContactDTO>>;
  allClientContacts: (id: string) => Promise<IApiResponseGenericDTO<IContactDTO[]>>;
  filter: (model: IGetContactsRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IContactDTO>>>;
  create: (model: IContactCreationDTO) => Promise<IApiResponseGenericDTO<IContactDTO>>;
  update: (contactId: string, model: IContactUpdateDTO) => Promise<IApiResponseGenericDTO<IContactDTO>>;
  delete: (contactId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const ContactContext = createContext<ContactContextProps | undefined>(undefined);

export const ContactProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (contactId: string): Promise<IApiResponseGenericDTO<IContactDTO>> => {
    return await apiClient.getRequest<IContactDTO>(`${ContactRoutes.BasePrefix}/${contactId}`);
  };

  const allClientContacts = async (id: string): Promise<IApiResponseGenericDTO<IContactDTO[]>> => {
    return await apiClient.getRequest<IContactDTO[]>(`${ContactRoutes.BasePrefix}/all/${id}`);
  };

  const filter = async (model: IGetContactsRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IContactDTO>>> => {
    return await apiClient.postRequest<IGetContactsRequestDTO, IPaginatedResultDTO<IContactDTO>>(
      `${ContactRoutes.BasePrefix}/${ContactRoutes.List}`,
      model
    );
  };

  const create = async (model: IContactCreationDTO): Promise<IApiResponseGenericDTO<IContactDTO>> => {
    return await apiClient.postRequest<IContactCreationDTO, IContactDTO>(ContactRoutes.BasePrefix, model);
  };

  const update = async (contactId: string, model: IContactUpdateDTO): Promise<IApiResponseGenericDTO<IContactDTO>> => {
    return await apiClient.putRequest<IContactUpdateDTO, IContactDTO>(`${ContactRoutes.BasePrefix}/${contactId}`, model);
  };

  const deleteContact = async (contactId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${ContactRoutes.BasePrefix}/${contactId}`);
  };

  return (
    <ContactContext.Provider value={{ getById, allClientContacts, filter, create, update, delete: deleteContact }}>
      {children}
    </ContactContext.Provider>
  );
};

export const useContactStore = () => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error('useContactStore must be used within a ContactProvider');
  }
  return context;
};
